import { ReactElement, useEffect, useMemo, useState } from 'react'
import { Button, Stack, Typography } from '@mui/material'
import { useControlStore, useSceneStore } from '@editor/stores'
import getSpatialClustersFromGltfModel from './utils'

export type CameraTarget = [number, number, number] | null

type CameraOriginSelectProps = {
  onCameraTargetChange: (position: CameraTarget) => void
}

const CameraOriginSelect = ({ onCameraTargetChange }: CameraOriginSelectProps): ReactElement => {
  const loadedGltfModels = useSceneStore(state => state.loadedGltfModels)
  const activeIfcFileGuid = useControlStore(state => state.activeIfcFileGuid)

  const clusters = useMemo(() => {
    if (!activeIfcFileGuid) return []
    const model = loadedGltfModels[activeIfcFileGuid]
    if (!model) return []
    return getSpatialClustersFromGltfModel(model)
  }, [activeIfcFileGuid, loadedGltfModels])

  const [selectedGroupIndex, setSelectedGroupIndex] = useState<number | null>(null)

  // Reset selection back to "Ursprung" when switching to another file.
  useEffect(() => {
    setSelectedGroupIndex(null)
  }, [activeIfcFileGuid])

  // Report current camera target to parent
  useEffect(() => {
    const position: CameraTarget =
      selectedGroupIndex == null || !clusters[selectedGroupIndex]
        ? null
        : clusters[selectedGroupIndex]
    onCameraTargetChange(position)
  }, [clusters, selectedGroupIndex, onCameraTargetChange])

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Objekte im Modell, die räumlich getrennt sind, wurden zu Gruppen zusammengefasst. Wechsel
        zwischen den Ansichten, um die richtige Gruppe zu finden.
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
        <Button
          variant={selectedGroupIndex === null ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setSelectedGroupIndex(null)}
        >
          Ursprung
        </Button>
        {clusters.map((_, index) => (
          <Button
            key={index}
            variant={selectedGroupIndex === index ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setSelectedGroupIndex(index)}
          >
            IFC {index + 1}
          </Button>
        ))}
      </Stack>
      {clusters.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Keine Meshes im Modell gefunden – Gruppen können nicht berechnet werden.
        </Typography>
      )}
    </>
  )
}

export default CameraOriginSelect
